"use client"

import { useCallback } from "react"

type NavigateWithSearch<TSearch extends object> = (options: {
  search: (prev: TSearch) => TSearch
  replace?: boolean
}) => void

type SearchPatch<TSearch extends object> =
  | Partial<TSearch>
  | ((prev: TSearch) => Partial<TSearch>)

type SearchMutationOptions = {
  replace?: boolean
}

function applySearchPatch<TSearch extends object>(
  prev: TSearch,
  patch: Partial<TSearch>,
): TSearch {
  const next: Partial<TSearch> = { ...prev }

  for (const [key, value] of Object.entries(patch) as Array<
    [keyof TSearch, TSearch[keyof TSearch] | undefined]
  >) {
    if (value === undefined) {
      delete next[key]
    } else {
      next[key] = value
    }
  }

  return next as TSearch
}

/**
 * Small helper hook for URL-owned UI state. Callers still use their route's
 * typed hooks to read/search/navigate; this keeps the repeated patch/clear
 * mechanics in one place while preserving route-specific validation.
 */
export function useUrlSyncedSearchState<TSearch extends object>(
  navigate: NavigateWithSearch<TSearch>,
) {
  const patchSearch = useCallback(
    (patch: SearchPatch<TSearch>, options: SearchMutationOptions = {}) => {
      navigate({
        search: (prev) => {
          const nextPatch = typeof patch === "function" ? patch(prev) : patch
          return applySearchPatch(prev, nextPatch)
        },
        replace: options.replace ?? true,
      })
    },
    [navigate],
  )

  const clearSearchKeys = useCallback(
    (keys: readonly (keyof TSearch)[], options: SearchMutationOptions = {}) => {
      navigate({
        search: (prev) => {
          const next: Partial<TSearch> = { ...prev }

          for (const key of keys) {
            delete next[key]
          }

          return next as TSearch
        },
        replace: options.replace ?? true,
      })
    },
    [navigate],
  )

  return {
    patchSearch,
    clearSearchKeys,
  }
}
